/**
 * Billing types — signup-billing-mvp Phase 2.
 *
 * Wire-format schemas + inferred types for the usage-tracking / pricing-model
 * surfaces: the per-user usage card (`BillingUsage`), the pre-run eligibility
 * gate (`RunEligibility`), the admin override taxonomy (`BillingOverrideKind`),
 * the pricing-config read + patch shapes (`PricingConfig` / `PricingConfigPatch`),
 * the derived-grant pair (`DerivedGrant`), the subscription-tier catalog entry
 * (`TierCatalogEntry`), and the admin usage-breakdown row (`UsageBreakdownRow`).
 *
 * **COGS-credit unit semantics** — every `*_cogs` / `monthly_grant_cogs` field
 * is a count of COGS credits, NOT dollars. The conversion factor is
 * `pricing_config.base_ratio_subscription_to_cogs` (the seed value is 20):
 * `base_ratio` COGS credits == $1 of Anthropic spend. So `monthly_grant_cogs`
 * is a credit ALLOWANCE, while `usage_events.anthropic_cost_usd` (raw $) and
 * `usage_events.cogs_cost` (the debited-credit cost) are the dollar / credit
 * sides of a single recorded event. `monthly_grant_cogs` is DERIVED, not stored
 * on `subscription_tiers` — it equals `base_tier.monthly_price_usd ×
 * usage_multiplier` and is denormalized onto `usage_balance` at
 * subscription-create + cron rollover (except when `grant_overridden = true`).
 * `stripe_product_id` / `stripe_price_id` are system-managed (created by the
 * Phase-5 backfill, never user-supplied).
 *
 * NO `ActionClass` — the action-class abstraction is dropped from the MVP
 * (research/feature.md § 9).
 *
 * All field names use the spec's EXACT spelling — NO `_cogs_usd`, NO
 * `cogs_debited`, NO `cogs_credits`.
 */
import { z } from 'zod';
// ── BillingUsage — the per-user usage card ─────────────────────────────────────
// Discriminated by the `admin` sentinel: an admin caller is unmetered (no grant,
// no period, no breakdown). A metered user carries the full grant/used/period
// block. The `pct` field is `monthly_grant_used_cogs / monthly_grant_cogs`
// clamped to `[0, 1]+` (it CAN exceed 1 — a user whose `usage_limiting_enabled`
// is false keeps running past 100%; the UI renders an over-quota banner).
export const billingUsageSchema = z.union([
    z.object({
        monthly_grant_cogs: z.number(),
        monthly_grant_used_cogs: z.number(),
        /** `monthly_grant_used_cogs / monthly_grant_cogs`, clamped `[0, 1]+`
         * (can exceed 1 for users with `usage_limiting_enabled = false`). */
        pct: z.number(),
        period_start: z.string(),
        period_end: z.string(),
        tier_id: z.string(),
        tier_display_name: z.string(),
        usage_limiting_enabled: z.boolean(),
        locked: z.boolean(),
    }),
    z.object({ admin: z.literal(true), unmetered: z.literal(true) }),
]);
// ── RunEligibility — the pre-run gate ──────────────────────────────────────────
export const runEligibilityReasonEnum = z.enum([
    'locked',
    'no_subscription',
    'allowance_exhausted',
]);
export const runEligibilitySchema = z.object({
    ok: z.boolean(),
    /** Present only when `ok === false`. */
    reason: runEligibilityReasonEnum.optional(),
    is_admin: z.boolean(),
    /** Remaining COGS credits in the current period. Present on a metered `ok`. */
    monthly_remaining_cogs: z.number().optional(),
});
// ── BillingOverrideKind — the admin override taxonomy ──────────────────────────
export const billingOverrideKindEnum = z.enum([
    'tier',
    'grant',
    'reset_subscription',
    'lock',
    'unlock',
    'set_usage_limiting',
    'set_founding_partner',
]);
// ── PricingConfig — the singleton `pricing_config` read + patch shapes ─────────
export const pricingConfigSchema = z.object({
    /** COGS credits per $1 of Anthropic spend. Seed value: 20. */
    base_ratio_subscription_to_cogs: z.number(),
    version: z.number().int(),
    updated_at: z.string(),
    updated_by_email: z.string().nullable().optional(),
});
export const pricingConfigPatchSchema = z.object({
    /** Bounded `[1.0, 500.0]`. `1.0` is the floor (1 credit == $1); `500.0` the
     * practical ceiling. The base-tier-must-be-1.0 invariant for `usage_multiplier`
     * is enforced server-side in the `upsert_pricing_config` RPC. */
    base_ratio_subscription_to_cogs: z.number().min(1.0).max(500.0),
    /** Optimistic-concurrency token — must match the current row's `version`. */
    version: z.number().int(),
});
// ── DerivedGrant — `{tier_id, monthly_grant_cogs}` pairs ───────────────────────
// Returned by `upsert_pricing_config` / `upsert_subscription_tier` so the
// admin UI can show "this change re-derives these grants".
export const derivedGrantSchema = z.object({
    tier_id: z.string(),
    /** COGS-credit allowance — = `base_tier.monthly_price_usd × usage_multiplier`. */
    monthly_grant_cogs: z.number(),
});
// ── TierCatalogEntry — a `subscription_tiers` row + the computed grant ─────────
export const tierCatalogEntrySchema = z.object({
    id: z.string(),
    display_name: z.string(),
    tagline: z.string(),
    monthly_price_usd: z.number().nullable(),
    usage_multiplier: z.number(),
    /** COMPUTED (not stored) — = `base_tier.monthly_price_usd × usage_multiplier`. */
    monthly_grant_cogs: z.number(),
    /** System-managed — created by the Phase-5 Stripe backfill, never user-set. */
    stripe_product_id: z.string().nullable(),
    /** System-managed — created by the Phase-5 Stripe backfill, never user-set. */
    stripe_price_id: z.string().nullable(),
    feature_bullets: z.array(z.string()),
    is_active: z.boolean(),
    sort_order: z.number().int(),
    version: z.number().int(),
    /** in-app-checkout Phase 1 — true means "Contact sales" CTA, no Stripe checkout. */
    contact_only: z.boolean(),
});
// ── UsageBreakdownRow — one row per (kind, model_or_tool) in the admin view ────
export const usageBreakdownRowSchema = z.object({
    kind: z.enum(['llm', 'tool']),
    model_or_tool: z.string(),
    event_count: z.number().int(),
    /** Sum of `usage_events.cogs_cost` for this group — COGS credits. */
    total_cogs: z.number(),
    /** Sum of `usage_events.anthropic_cost_usd` — raw $. Admin-only; never
     * shown to a user. */
    total_anthropic_cost_usd: z.number(),
    total_input_tokens: z.number().int(),
    total_output_tokens: z.number().int(),
});
// NO ActionClass — the action-class abstraction is dropped (research/feature.md § 9).
// ═════════════════════════════════════════════════════════════════════════════
// PHASE 5 — Stripe webhook event narrowed schemas + HTTP route req/resp shapes
// ═════════════════════════════════════════════════════════════════════════════
// Cites: research/phase-5.md § 5.3 for the 5 Stripe event type literals.
// Cites: research/phase-5.md § 4.2-4.3 for the RPC return shapes + tier shape.
//
// The Stripe Node SDK already ships `Stripe.Event` types, but those are wide
// untagged unions. These narrowed Zod schemas validate the SHAPE of the
// inbound `event.data.object` for the 5 events we actually handle in the
// webhook (`customer.subscription.{created,updated,deleted}` +
// `invoice.{paid,payment_failed}`) so the handler can typed-discriminate on
// `event.type` and trust the inner shape.
//
// `passthrough()` is used on inner objects because Stripe events carry many
// fields we don't read; we only assert the fields we DO read are present + typed.
// ── Stripe webhook event narrowed schemas ─────────────────────────────────────
/** Inner subscription-object shape — minimal field set used by Phase 5 handlers. */
const stripeSubscriptionObjectSchema = z
    .object({
    id: z.string(),
    customer: z.string(),
    status: z.string(),
    items: z.object({
        data: z.array(z
            .object({
            price: z.object({ id: z.string() }).passthrough(),
        })
            .passthrough()),
    }),
    current_period_start: z.number().int(),
    current_period_end: z.number().int(),
    metadata: z.record(z.string(), z.string()).optional(),
})
    .passthrough();
/** Inner invoice-object shape — minimal field set used by Phase 5 handlers. */
const stripeInvoiceObjectSchema = z
    .object({
    id: z.string(),
    customer: z.string(),
    subscription: z.string().nullable().optional(),
    amount_paid: z.number().int().optional(),
    amount_due: z.number().int().optional(),
    status: z.string().optional(),
    metadata: z.record(z.string(), z.string()).optional(),
})
    .passthrough();
export const stripeSubscriptionCreatedEventSchema = z
    .object({
    id: z.string(),
    type: z.literal('customer.subscription.created'),
    data: z.object({ object: stripeSubscriptionObjectSchema }),
})
    .passthrough();
export const stripeSubscriptionUpdatedEventSchema = z
    .object({
    id: z.string(),
    type: z.literal('customer.subscription.updated'),
    data: z.object({ object: stripeSubscriptionObjectSchema }),
})
    .passthrough();
export const stripeSubscriptionDeletedEventSchema = z
    .object({
    id: z.string(),
    type: z.literal('customer.subscription.deleted'),
    data: z.object({ object: stripeSubscriptionObjectSchema }),
})
    .passthrough();
export const stripeInvoicePaidEventSchema = z
    .object({
    id: z.string(),
    type: z.literal('invoice.paid'),
    data: z.object({ object: stripeInvoiceObjectSchema }),
})
    .passthrough();
export const stripeInvoicePaymentFailedEventSchema = z
    .object({
    id: z.string(),
    type: z.literal('invoice.payment_failed'),
    data: z.object({ object: stripeInvoiceObjectSchema }),
})
    .passthrough();
export const stripeWebhookEventSchema = z.discriminatedUnion('type', [
    stripeSubscriptionCreatedEventSchema,
    stripeSubscriptionUpdatedEventSchema,
    stripeSubscriptionDeletedEventSchema,
    stripeInvoicePaidEventSchema,
    stripeInvoicePaymentFailedEventSchema,
]);
// ── Tier catalog request/response (admin CRUD) ────────────────────────────────
/** ID regex matches the Phase 2 migration CHECK constraint on `subscription_tiers.id`. */
const tierIdSchema = z.string().regex(/^[a-z][a-z0-9-]{2,30}$/, {
    message: 'tier id must be 3-31 chars, lowercase letters/digits/hyphen, starting with a letter',
});
export const tierCreateRequestSchema = z.object({
    id: tierIdSchema,
    display_name: z.string().min(1),
    tagline: z.string().default(''),
    monthly_price_usd: z.number().nonnegative().nullable(),
    usage_multiplier: z.number().positive(),
    feature_bullets: z.array(z.string()).default([]),
    sort_order: z.number().int().default(0),
    is_active: z.boolean().default(true),
    /** in-app-checkout Phase 1 — true means "Contact sales" CTA, no Stripe checkout. */
    contact_only: z.boolean().default(false),
});
export const tierUpdateRequestSchema = z.object({
    id: tierIdSchema,
    display_name: z.string().min(1),
    tagline: z.string().default(''),
    monthly_price_usd: z.number().nonnegative().nullable(),
    usage_multiplier: z.number().positive(),
    feature_bullets: z.array(z.string()).default([]),
    sort_order: z.number().int().default(0),
    is_active: z.boolean().default(true),
    /** Optimistic-concurrency token — must match the current row's `version`. */
    version: z.number().int(),
    /** in-app-checkout Phase 1 — true means "Contact sales" CTA, no Stripe checkout. */
    contact_only: z.boolean().default(false),
});
/** Full tier row + the computed `derived_monthly_grant_cogs`. Mirrors RPC return. */
export const tierResponseSchema = z.object({
    id: z.string(),
    display_name: z.string(),
    tagline: z.string(),
    monthly_price_usd: z.number().nullable(),
    usage_multiplier: z.number(),
    feature_bullets: z.array(z.string()),
    sort_order: z.number().int(),
    is_active: z.boolean(),
    version: z.number().int(),
    stripe_product_id: z.string().nullable(),
    stripe_price_id: z.string().nullable(),
    /** = `base_tier.monthly_price_usd × usage_multiplier`. */
    derived_monthly_grant_cogs: z.number(),
    /** in-app-checkout Phase 1 — true means "Contact sales" CTA, no Stripe checkout. */
    contact_only: z.boolean(),
});
// ── Pricing config request/response (admin singleton update) ─────────────────
export const pricingConfigUpdateRequestSchema = z.object({
    base_ratio_subscription_to_cogs: z.number().min(1.0).max(500.0),
    /** Optimistic-concurrency token — must match the current row's `version`. */
    version: z.number().int(),
});
export const pricingConfigResponseSchema = z.object({
    base_ratio_subscription_to_cogs: z.number(),
    version: z.number().int(),
    updated_at: z.string(),
    updated_by_email: z.string().nullable().optional(),
    /** Derived grants per active tier — re-computed on read by the GET handler. */
    derived_grants: z.array(derivedGrantSchema),
});
// ── Admin override request ────────────────────────────────────────────────────
export const adminOverrideRequestSchema = z.object({
    targetUserId: z.string().uuid(),
    kind: billingOverrideKindEnum,
    /** Kind-specific value envelope — validated by the `apply_admin_override` RPC. */
    value: z.unknown(),
    reason: z.string().min(1).max(500).nullable().optional(),
    /** Idempotency key — re-running with the same key returns `idempotent_replay: true`. */
    idempotency_key: z.string().min(1).max(200),
});
// ── Stripe Checkout session ──────────────────────────────────────────────────
export const checkoutSessionRequestSchema = z.object({
    tier_id: z.string(),
    /** Optional shared-deal id — when set, post-checkout redirect lands on the
     * deal page instead of the cockpit. */
    conversation_share_id: z.string().uuid().optional(),
});
export const checkoutSessionResponseSchema = z.object({
    url: z.string().url(),
});
// ── Stripe Customer Portal session ────────────────────────────────────────────
export const portalSessionResponseSchema = z.object({
    url: z.string().url(),
});
// ── Admin status (GET /api/users/me/admin-status) ─────────────────────────────
export const adminStatusResponseSchema = z.object({
    is_admin: z.boolean(),
});
// ── Invoice list (GET /api/billing/invoices) ──────────────────────────────────
const invoiceSummarySchema = z.object({
    id: z.string(),
    number: z.string().nullable(),
    amount_paid: z.number().int(),
    currency: z.string(),
    status: z.string(),
    created: z.number().int(),
    hosted_invoice_url: z.string().nullable(),
    invoice_pdf: z.string().nullable(),
    /** Stripe `invoice.billing_reason`: subscription_create | subscription_cycle |
     * subscription_update | manual | null. Additive — nullable so pre-feature
     * invoices without this field still parse. */
    billing_reason: z.string().nullable(),
});
export const invoicesResponseSchema = z.object({
    data: z.array(invoiceSummarySchema),
});
// ── Optimistic-lock error envelope (409 response shape) ──────────────────────
// Decision 6 (phase 5 design): the 409 response carries the current row so the
// admin UI's mutation `onError` handler can refresh its local cache immediately
// to truth before the operator re-edits.
export const optimisticLockFailedErrorSchema = z.object({
    error: z.object({
        code: z.literal('OPTIMISTIC_LOCK_FAILED'),
        message: z.string(),
        details: z.object({
            /** The current row at the time the conflict was detected. Either a tier or
             * the pricing-config singleton, depending on the route that surfaced the
             * conflict. Both shapes parse cleanly; the consumer narrows by route. */
            current_row: z.union([tierResponseSchema, pricingConfigResponseSchema]),
        }),
    }),
});
// ═════════════════════════════════════════════════════════════════════════════
// IN-APP-CHECKOUT — Payment + Bespoke request/response schemas
// ═════════════════════════════════════════════════════════════════════════════
// Cites: research/phase-1.md §9.6-§9.13 for all proposed shapes.
//
// PaymentIntent — in-app (Stripe Elements) subscription flow. The `status`
// discriminated union lets web narrow `requires_action` (3DS) → call
// `stripe.confirmCardPayment(client_secret)` vs `succeeded` → navigate away.
//
// SetupIntent — off-session card-save for future renewals. Response carries
// only `client_secret`; web calls `stripe.confirmCardSetup(client_secret)`.
//
// ProrationPreview — GET /api/billing/proration-preview?tier_id=...  response.
// All dollar amounts are integer CENTS (Stripe upcoming-invoice amounts are
// cents; CLAUDE.md "All prices in integer cents"). NOT `_cogs` (real money).
//
// Bespoke — contact-sales / enterprise path for `contact_only=true` tiers.
// Pairs with the `BESPOKE_REQUESTED` lifecycle event (lifecycle-events.types.ts).
//
// CheckoutError — mirrors `optimisticLockFailedErrorSchema` envelope; carries
// card-decline / SCA / checkout-generic failure codes from the payment routes.
export const paymentIntentRequestSchema = z.object({
    /** The target subscription tier id. Bare `z.string()` mirrors checkoutSessionRequest (line 362). */
    tier_id: z.string(),
});
export const paymentIntentResponseSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('succeeded'),
        /** Stripe Subscription id on immediate success (no 3DS challenge). */
        subscription_id: z.string(),
    }),
    z.object({
        status: z.literal('requires_action'),
        /** Stripe PaymentIntent client secret — pass to `stripe.confirmCardPayment()`. */
        client_secret: z.string(),
    }),
]);
export const setupIntentRequestSchema = z.object({
    /** The target subscription tier id (optional — setup-intent may be tier-agnostic). */
    tier_id: z.string().optional(),
});
export const setupIntentResponseSchema = z.object({
    /** Stripe SetupIntent client secret — pass to `stripe.confirmCardSetup()`. */
    client_secret: z.string(),
});
export const prorationPreviewRequestSchema = z.object({
    /** The target tier id for the mid-cycle upgrade/downgrade preview. */
    tier_id: z.string(),
});
export const prorationPreviewResponseSchema = z.object({
    /** Amount the customer owes on the next invoice, in integer CENTS. */
    amount_due_cents: z.number().int(),
    /** Total of the next invoice after proration credits, in integer CENTS. */
    next_invoice_total_cents: z.number().int(),
    /** Unix timestamp of the proration anchor date used in the calculation. */
    proration_date: z.number().int(),
});
export const bespokeRequestSchema = z.object({
    /** The tier the user is requesting access to (should be a contact_only tier). */
    tier_id: z.string(),
    /** Optional free-text note from the user (e.g., "We have 12 underwriters"). */
    note: z.string().max(2000).optional(),
});
export const bespokeResponseSchema = z.object({
    /** Confirmation that the bespoke request was recorded. */
    submitted: z.literal(true),
});
// ── Checkout / payment error envelope (4xx response shape) ───────────────────
// Mirrors `optimisticLockFailedErrorSchema`'s `{ error: { code, message, details } }`
// envelope exactly (billing.types.ts:412-424). Code values: CARD_DECLINED
// (card processor hard decline), PAYMENT_REQUIRES_ACTION (unhandled 3DS —
// returned when the caller didn't do the client-side challenge),
// CHECKOUT_FAILED (generic / Stripe configuration failure).
export const billingErrorCodeEnum = z.enum([
    'CARD_DECLINED',
    'PAYMENT_REQUIRES_ACTION',
    'CHECKOUT_FAILED',
]);
export const checkoutErrorResponseSchema = z.object({
    error: z.object({
        code: billingErrorCodeEnum,
        message: z.string(),
        details: z.object({}).passthrough(),
    }),
});
// ═════════════════════════════════════════════════════════════════════════════
// IN-APP-CHECKOUT Phase 3 — plan-change / cancel / resume APPLY endpoints
// ═════════════════════════════════════════════════════════════════════════════
// Cites: in-app-checkout ADR 0009 — Phase 2 shipped proration-PREVIEW but
// not the APPLY counterparts; these are the 3 additive APPLY schemas.
//
// plan change: PUT /api/billing/subscription
//   body:     { tier_id: string }   (the NEW tier)
//   response: { subscription_id, status }
//
// cancel: DELETE /api/billing/subscription
//   no body.
//   response: { cancel_at: string | null }  (ISO timestamp; null if not scheduled)
//
// resume: PUT /api/billing/subscription/resume
//   no body.
//   response: { cancel_at: null }  (always null after resume)
/** PUT /api/billing/subscription body — the target tier to switch to. */
export const planChangeRequestSchema = z
    .object({
    /** The id of the tier to switch the subscription to. */
    tier_id: z.string().min(1),
})
    .strict();
/**
 * PUT /api/billing/subscription response — the applied subscription state.
 * Minimal projection: the web needs `subscription_id` to update local cache
 * and `status` to confirm the plan is now active (vs. 'incomplete', etc.).
 */
export const planChangeResponseSchema = z
    .object({
    /** Stripe Subscription id (unchanged; the subscription is mutated, not replaced). */
    subscription_id: z.string(),
    /** Stripe Subscription status after the update (e.g. "active"). */
    status: z.string(),
})
    .strict();
/**
 * Shared response shape for cancel + resume — both return `{ cancel_at }`.
 * Cancel: `cancel_at` = ISO timestamp of the scheduled cancellation.
 * Resume: `cancel_at` = null (cancellation cleared).
 * The webhook's `handleSubscriptionUpdated` is the authoritative writer of
 * `usage_balance.cancel_at`; these endpoints return the Stripe-computed value.
 */
export const subscriptionMutationResponseSchema = z
    .object({
    /**
     * ISO timestamp of the scheduled cancellation, or null when no cancellation
     * is scheduled (including after a resume).
     */
    cancel_at: z.string().nullable(),
})
    .strict();
/**
 * DELETE /api/billing/subscription response alias.
 * Same shape as `subscriptionMutationResponseSchema`; aliased for clarity on
 * the web import side.
 */
export const cancelSubscriptionResponseSchema = subscriptionMutationResponseSchema;
/**
 * PUT /api/billing/subscription/resume response alias.
 * Same shape as `subscriptionMutationResponseSchema`; `cancel_at` is always
 * null on a successful resume (Stripe clears `cancel_at_period_end`).
 */
export const resumeSubscriptionResponseSchema = subscriptionMutationResponseSchema;
